/**
 * Push #17 — global Cmd-K search.
 */

import { get } from './_shared.ts';

// ---------------------------------------------------------------------
// Push #17 — global Cmd-K search
// ---------------------------------------------------------------------

/**
 * 'request' kind is retained in the union — it still tags the "Open inbox"
 * Suggested row in GlobalSearch, which navigates to /admin/inbox (the live
 * triage surface). The Migration H drop only removed the backend's `requests`
 * search-result group (sys__request table is gone); the Suggested entry +
 * route are unaffected.
 *
 * Push 4-1d-u (2026-07-06): the `catalog_connector` / `catalog_prompt` /
 * `catalog_model` kinds are retained in the union because the Suggested rows
 * in GlobalSearch ("Browse the market") still tag with a catalog kind for
 * iconography. The backend `/api/search` endpoint no longer returns any
 * `catalog_*` search results — those are Market-page-only surfaces now.
 */
export type SearchResultKind =
  | 'connector'
  | 'prompt'
  | 'model'
  | 'dispatch'
  | 'catalog_connector'
  | 'catalog_prompt'
  | 'catalog_model'
  | 'user'
  | 'permission_set'
  | 'activity'
  | 'request'
  // Push 4-1d-k-cont Class C additions (2026-07-02) — user-scope only. Chat
  // sessions, library documents, and notifications now surface in Cmd-K
  // alongside the existing capability groups. Each group scopes strictly to
  // the caller via account_user_id / created_by predicates INSIDE the
  // withScope block (RLS already auto-scopes account_id).
  | 'chat_session'
  | 'document'
  | 'notification';

export interface SearchResult {
  id: string;
  title: string;
  subtitle: string;
  to: string;
  kind: SearchResultKind;
  vendor_slug?: string;
}

export interface UserSearchResponse {
  scope: 'user';
  query: string;
  my_connectors: SearchResult[];
  my_prompts: SearchResult[];
  my_models: SearchResult[];
  dispatches: SearchResult[];
  // Push 4-1d-k-cont Class C additions (2026-07-02) — user-scope groups
  // filling audit gaps. Admin scope doesn't need these; admin surfaces
  // notifications + docs + chats through other admin pages already.
  chat_sessions: SearchResult[];
  documents: SearchResult[];
  notifications: SearchResult[];
}

/**
 * Phase 2 Finishing Pass Round 3 Item 2 (2026-06-25): the `requests` field
 * was dropped from this interface after Migration H removed the sys__request
 * table. The backend `/api/search` endpoint no longer returns a `requests`
 * group; the field on the interface had become a stale lint relic. GlobalSearch
 * was the only consumer; the addGroup('Pending requests', data.requests) line
 * was removed in lockstep.
 *
 * Push 4-1d-u (2026-07-06): the `catalog` field was likewise dropped from
 * both response shapes — search now surfaces ONLY account-scoped records
 * (workspace_selfservice.*). workspace_directory template tables are visible
 * on the Market page (/market, /admin/market) only.
 */
export interface AdminSearchResponse {
  scope: 'admin';
  query: string;
  users: SearchResult[];
  permission_sets: SearchResult[];
  connectors: SearchResult[];
  prompts: SearchResult[];
  models: SearchResult[];
  activity: SearchResult[];
}

export function fetchSearch(
  query: string,
  scope: 'user' | 'admin',
): Promise<UserSearchResponse | AdminSearchResponse> {
  const params = new URLSearchParams({ q: query, scope });
  return get<UserSearchResponse | AdminSearchResponse>(`/api/search?${params.toString()}`);
}
